/**
 * AgentStatusBar Component
 * Horizontal bar showing all available agent icons
 */

import React from 'react'
import type { Agent, AgentType } from '../../types'
import { AGENT_TYPE_CONFIG } from './agentConfig'

interface AgentStatusBarProps {
  /**
   * List of available agents
   */
  agents: Agent[]
  /**
   * Currently selected agent type
   */
  currentAgentType?: AgentType
  /**
   * Callback when an agent is selected
   */
  onSelectAgent: (agentType: AgentType) => void
}

/**
 * Horizontal bar displaying agent icons with status indicators
 */
export const AgentStatusBar: React.FC<AgentStatusBarProps> = ({
  agents,
  currentAgentType,
  onSelectAgent,
}) => {
  // Get all agent types from config for consistent ordering
  const agentTypes = Object.keys(AGENT_TYPE_CONFIG) as AgentType[]

  return (
    <div className="flex items-center justify-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2">
      {agentTypes.map((agentType) => {
        const config = AGENT_TYPE_CONFIG[agentType]
        const agent = agents.find((a) => a.agentType === agentType)
        const isActive = currentAgentType === agentType
        const isAvailable = agent?.isActive ?? false

        return (
          <button
            key={agentType}
            type="button"
            onClick={() => onSelectAgent(agentType)}
            disabled={!isAvailable}
            className={`
              group relative flex h-10 w-10 items-center justify-center rounded-full text-xl
              transition-all duration-200
              ${
                isActive
                  ? 'bg-blue-100 ring-2 ring-blue-500 ring-offset-2'
                  : isAvailable
                    ? 'bg-gray-100 hover:bg-gray-200'
                    : 'cursor-not-allowed bg-gray-100 opacity-50'
              }
            `}
            title={`${config.name} - ${config.description}`}
            aria-label={`Select ${config.name} agent`}
            aria-pressed={isActive}
          >
            <span>{config.emoji}</span>

            {/* Status dot */}
            <span
              className={`
                absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white
                ${isAvailable ? 'bg-green-500' : 'bg-gray-400'}
              `}
            />

            {/* Tooltip */}
            <span
              className="
                pointer-events-none absolute -bottom-8 left-1/2 z-10 -translate-x-1/2
                whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white
                opacity-0 transition-opacity group-hover:opacity-100
              "
            >
              {config.name}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default AgentStatusBar
